import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ActionSheet, Button, Dialog, NavBar, Popup} from 'antd-mobile';
import {CheckOutline} from 'antd-mobile-icons';
import {DspPath} from '../../../enums/DeliveryNavigatePathEnum';
import {DspValidationType} from '../../../enums/DspValidationType';
import {useDspValidation} from '../../../hooks/useDspValidation';
import {useBarCodeScanner} from '../../../components/BarCodeScanner';
import {isTrackingNumber, showTrackingNumberError} from '../../../utils/BarCodeUtil';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {ArrowLeftOutlined} from "@ant-design/icons";
import DynamSoftScanComponent from "../../../components/ScanComponent/DynamSoftScanComponent";
import { ExceptionDictionaryType, ExceptionSubReasonType } from '../../delivery/graphql/queries/getExceptionDictionaryForDelivery';
import { getExceptionDictionaryForDsp } from '../../delivery/graphql/queries/getExceptionDictionaryForDsp';
import {getExceptionTypeText, getLocalizedText} from '../../../utils/i18nUtils';
import DspPackageCard from '../../../components/DspPackageCard/DspPackageCard';
import {DspPackageDetail, GET_DSP_SCAN_DETAIL} from '../graphql/queries/getPackageDetail';
import {useQuery} from '@apollo/client';

interface DspPackageScanPageProps {}

const DspPackageScanPage: React.FC<DspPackageScanPageProps> = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    const {scanResult, setIsVisible} = useBarCodeScanner();
    
    // 异常选择弹窗状态
    const [exceptionActionSheetVisible, setExceptionActionSheetVisible] = useState(false);
    const [exceptionDictionaries, setExceptionDictionaries] = useState<ExceptionDictionaryType[]>([]);
    const [currentTrackingNumber, setCurrentTrackingNumber] = useState<string>('');
    const [selectedExceptionForSubReasons, setSelectedExceptionForSubReasons] = useState<ExceptionDictionaryType | null>(null);
    const [selectedSubReasonCodes, setSelectedSubReasonCodes] = useState<Set<string>>(new Set());
    
    // 包裹信息卡片状态
    const [packageCardVisible, setPackageCardVisible] = useState(false);
    const [currentPackageDetail, setCurrentPackageDetail] = useState<DspPackageDetail | null>(null);
    const [currentValidationType, setCurrentValidationType] = useState<DspValidationType | null>(null);
    
    // 查询包裹详情
    const { refetch: refetchPackageDetail } = useQuery(GET_DSP_SCAN_DETAIL, {
        skip: true
    });

    const urlValidationType = searchParams.get('validationType') as DspValidationType;
    const urlSuccessPath = searchParams.get('successPath');
    const urlTitle = searchParams.get('title') || t('pages.dsp.home.trackPackage');

    const buildNavigationParams = (extraParams?: Record<string, string>) => {
        const queryParams = new URLSearchParams();
        if (urlTitle) {
            queryParams.set('title', urlTitle);
        }
        if (urlValidationType) {
            queryParams.set('validationType', urlValidationType);
        }
        // 添加额外的参数
        if (extraParams) {
            Object.entries(extraParams).forEach(([key, value]) => {
                queryParams.set(key, value);
            });
        }
        return queryParams;
    };

    // 获取异常字典数据
    useEffect(() => {
        const fetchExceptionDictionaries = async () => {
            try {
                const data = await getExceptionDictionaryForDsp();
                setExceptionDictionaries(data);
            } catch (error) {
                console.error('Failed to fetch exception dictionaries:', error);
            }
        };
        
        if (urlValidationType === DspValidationType.EXCEPTION_REPORT) {
            fetchExceptionDictionaries();
        }
    }, [urlValidationType]);

    // 控制扫描器的显示/隐藏
    useEffect(() => {
        setIsVisible(true);
        return () => {
            setIsVisible(false);
        }
    }, [setIsVisible]);

    // 监听扫描结果
    useEffect(() => {
        if (scanResult) {
            handleScanSuccess(scanResult);
        }
    }, [scanResult]);

    // 使用自定义的验证hook
    const { validatePackage, loading, isDialogVisible } = useDspValidation({
        onSuccess: async (trackingNumber) => {
            // 当urlValidationType为空时，视为查询包裹场景，直接跳转到详情页面
            if (!urlValidationType) {
                navigate(DspPath.DELIVERY_DSP_TRACK_DETAIL.replace(':trackingNumber', trackingNumber));
                return;
            }
            
            // 验证成功后，先获取包裹详情，然后显示卡片
            try {
                const result = await refetchPackageDetail({ trackingNumber });
                if (result.data?.getDspPackageDetailByTrackingNumber) {
                    setCurrentPackageDetail(result.data.getDspPackageDetailByTrackingNumber);
                    setCurrentTrackingNumber(result.data.getDspPackageDetailByTrackingNumber.trackingNumber);
                    setCurrentValidationType(urlValidationType);
                    setPackageCardVisible(true);
                }
            } catch (error) {
                console.error('Failed to fetch package detail:', error);
                Dialog.alert({
                    content: `pages.dsp.validation.errors.notExist`,
                    confirmText: t('pages.dsp.packageScan.confirm'),
                    onConfirm: () => {
                        // 可以在这里添加额外的确认逻辑
                    }
                });
            }
        },
        onError: (errorCode, trackingNumber) => {
            // 错误处理已经在hook中完成，这里可以添加额外的逻辑
            console.error(`Validation failed for ${trackingNumber}: ${errorCode}`);
        },
        onConfirm: async (trackingNumber, errorCode) => {
            console.log(`User confirmed for ${trackingNumber} with error ${errorCode}`);
            // 用户确认后的处理逻辑
            if (errorCode === 'modifyPod' || errorCode === 'notSignedYet') {
                // 对于这两种错误码，先获取包裹详情并显示卡片
                try {
                    const result = await refetchPackageDetail({ trackingNumber });
                    if (result.data?.getDspPackageDetailByTrackingNumber) {
                        setCurrentPackageDetail(result.data.getDspPackageDetailByTrackingNumber);
                        setCurrentTrackingNumber(result.data.getDspPackageDetailByTrackingNumber.trackingNumber);
                        // 根据错误码设置对应的验证类型
                        if (errorCode === 'modifyPod') {
                            setCurrentValidationType(DspValidationType.POD_MODIFY);
                        } else if (errorCode === 'notSignedYet') {
                            setCurrentValidationType(DspValidationType.SIGN_RECEIPT);
                        }
                        setPackageCardVisible(true);
                    }
                } catch (error) {
                    console.error('Failed to fetch package detail:', error);
                    // 如果获取包裹详情失败，直接跳转并携带title参数
                    if (errorCode === 'modifyPod') {
                        const targetPath = DspPath.DELIVERY_DSP_DELIVERED_POD.replace(':trackingNumber', trackingNumber);
                        const queryParams = buildNavigationParams();
                        navigate(`${targetPath}?${queryParams.toString()}`);
                    } else if (errorCode === 'notSignedYet') {
                        const targetPath = DspPath.DELIVERY_DSP_DELIVERED_POD.replace(':trackingNumber', trackingNumber);
                        const queryParams = buildNavigationParams();
                        navigate(`${targetPath}?${queryParams.toString()}`);
                    }
                }
            }
        },
        onCancel: (trackingNumber, errorCode) => {

        },
        showDefaultError: true
    });

    const searchPackage = (trackingNumber: string) => {
        validatePackage(trackingNumber, urlValidationType);
    };

    const handleScanSuccess = (trackingNumber: string) => {
        console.log('Scanned tracking number:', trackingNumber);

        if (!isTrackingNumber(trackingNumber)) {
            showTrackingNumberError(trackingNumber);
            return;
        }

        searchPackage(trackingNumber.trim());
    };

    // 处理包裹卡片确认按钮点击
    const handlePackageCardConfirm = () => {
        setPackageCardVisible(false);
        
        // 使用当前的验证类型而不是URL中的验证类型
        const validationType = currentValidationType || urlValidationType;
        
        if (validationType === DspValidationType.EXCEPTION_REPORT) {
            // 异常上报场景，显示异常选择弹窗
            setExceptionActionSheetVisible(true);
        } else if (validationType === DspValidationType.POD_MODIFY && currentTrackingNumber) {
            // POD修改场景，跳转到修改POD页面
            const targetPath = DspPath.DELIVERY_DSP_DELIVERED_POD.replace(':trackingNumber', currentTrackingNumber);
            const queryParams = buildNavigationParams();
            navigate(`${targetPath}?${queryParams.toString()}`);
        } else if (validationType === DspValidationType.SIGN_RECEIPT && currentTrackingNumber) {
            // 签收场景，跳转到签收页面
            const targetPath = DspPath.DELIVERY_DSP_DELIVERED_POD.replace(':trackingNumber', currentTrackingNumber);
            const queryParams = buildNavigationParams();
            navigate(`${targetPath}?${queryParams.toString()}`);
        } else if (urlSuccessPath && currentTrackingNumber) {
            // 其他场景，跳转到目标页面并携带title参数
            const targetPath = urlSuccessPath.replace(':trackingNumber', currentTrackingNumber);
            const queryParams = buildNavigationParams();
            navigate(`${targetPath}?${queryParams.toString()}`);
        }
    };

    // 处理包裹卡片取消按钮点击
    const handlePackageCardCancel = () => {
        setPackageCardVisible(false);
        setCurrentPackageDetail(null);
        setCurrentTrackingNumber('');
        setCurrentValidationType(null);
    };

    // 处理异常选择点击
    const handleExceptionActionClick = (action: ExceptionDictionaryType) => {
        setExceptionActionSheetVisible(false);
        const subReasons = action.exceptionSubReasons;
        if (subReasons && subReasons.length > 0) {
            setSelectedExceptionForSubReasons(action);
            setSelectedSubReasonCodes(new Set());
        } else {
            navigateWithException(action, undefined);
        }
    };

    // 带异常信息跳转到目标页面
    const navigateWithException = (action: ExceptionDictionaryType, exceptionSubReasons?: ExceptionSubReasonType[]) => {
        if (!urlSuccessPath || !currentTrackingNumber) return;
        const targetPath = urlSuccessPath.replace(':trackingNumber', currentTrackingNumber);
        const extraParams: Record<string, string> = {
            exceptionReason: encodeURIComponent(JSON.stringify(action))
        };
        if (exceptionSubReasons && exceptionSubReasons.length > 0) {
            extraParams.exceptionSubReasons = encodeURIComponent(JSON.stringify(exceptionSubReasons));
        }
        const queryParams = buildNavigationParams(extraParams);
        navigate(`${targetPath}?${queryParams.toString()}`);
    };

    // 处理二级原因确认
    const handleSubReasonConfirm = (action: ExceptionDictionaryType, subReasons: ExceptionSubReasonType[]) => {
        setSelectedExceptionForSubReasons(null);
        setSelectedSubReasonCodes(new Set());
        navigateWithException(action, subReasons);
    };

    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => navigate(DspPath.DELIVERY_DSP_HOME)}
                    backIcon={<ArrowLeftOutlined/>}
                >
                    {urlTitle}
                </NavBar>
            </div>
            <div className="flex-1 relative">
                <DynamSoftScanComponent
                    onScanSuccess={handleScanSuccess}
                    showManualInputIcon={true}
                    isPaused={packageCardVisible || exceptionActionSheetVisible || selectedExceptionForSubReasons !== null || isDialogVisible}
                />
            </div>
            
            {/* 包裹信息卡片 */}
            {packageCardVisible && currentPackageDetail && (
                <DspPackageCard
                    packageDetail={currentPackageDetail}
                    validationType={currentValidationType || urlValidationType}
                    onConfirm={handlePackageCardConfirm}
                    onCancel={handlePackageCardCancel}
                />
            )}
            
            {/* 异常选择弹窗 */}
            <ActionSheet
                visible={exceptionActionSheetVisible}
                extra={
                    <div className="text-gray-500">
                        <h3 className="text-center">{t('pages.delivery.orderDetail.failed')}</h3>
                        <span>{t('pages.delivery.orderDetail.failedDesc')}</span>
                    </div>
                }
                cancelText={t('pages.delivery.detail.cancel')}
                onClose={() => setExceptionActionSheetVisible(false)}
                actions={exceptionDictionaries.map(action => ({
                    text: (
                        <div>
                            <div className="text-purple-700 font-medium text-lg">
                                {getExceptionTypeText(action)}
                            </div>
                        </div>
                    ),
                    key: action.exceptionReasonCode,
                    onClick: () => handleExceptionActionClick(action)
                }))}
            />

            {/* 二级原因选择弹窗（参考 OrderDetailTemporaryPage 样式） */}
            <Popup
                visible={selectedExceptionForSubReasons !== null}
                position="bottom"
                onMaskClick={() => {
                    setSelectedExceptionForSubReasons(null);
                    setSelectedSubReasonCodes(new Set());
                }}
                bodyStyle={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
            >
                <div className="p-4 pb-safe">
                    <h3 className="text-center text-gray-800 font-semibold text-base py-3">
                        {selectedExceptionForSubReasons ? t('pages.delivery.orderDetail.wrongAddressReason') : ''}
                    </h3>
                    <div className="max-h-60 overflow-y-auto mb-4 space-y-2">
                        {(selectedExceptionForSubReasons?.exceptionSubReasons ?? []).map(sub => {
                            const checked = selectedSubReasonCodes.has(sub.code);
                            return (
                                <div
                                    key={sub.code}
                                    className={`flex items-center justify-between py-3 px-4 rounded-xl border-2 bg-white active:opacity-90 ${
                                        checked ? 'border-purple-600' : 'border-gray-200'
                                    }`}
                                    onClick={() => {
                                        setSelectedSubReasonCodes(prev => {
                                            const next = new Set(prev);
                                            if (checked) next.delete(sub.code);
                                            else next.add(sub.code);
                                            return next;
                                        });
                                    }}
                                >
                                    <span className={`text-base ${checked ? 'text-purple-600 font-medium' : 'text-gray-500'}`}>
                                        {getLocalizedText({ zh: sub.textZh, en: sub.textEn, es: sub.textEs }, sub.textEn)}
                                    </span>
                                    <div
                                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            checked ? 'bg-purple-600' : 'border-2 border-gray-300 bg-white'
                                        }`}
                                    >
                                        {checked ? (
                                            <CheckOutline className="text-white" style={{ fontSize: 16 }} />
                                        ) : (
                                            <CheckOutline className="text-gray-300" style={{ fontSize: 16 }} />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Button
                        block
                        color="primary"
                        className="w-full"
                        style={{ '--background-color': '#7c3aed', '--border-radius': '8px' } as React.CSSProperties}
                        disabled={selectedSubReasonCodes.size === 0}
                        onClick={() => {
                            if (selectedExceptionForSubReasons) {
                                const subReasons = (selectedExceptionForSubReasons.exceptionSubReasons ?? []).filter(
                                    sub => selectedSubReasonCodes.has(sub.code)
                                );
                                handleSubReasonConfirm(selectedExceptionForSubReasons, subReasons);
                            }
                        }}
                    >
                        {t('pages.delivery.orderDetail.confirm')}
                    </Button>
                </div>
            </Popup>
        </div>
    );
};

export default DspPackageScanPage;
